/**
 * Search URL-state codec — frontend.md §2.2, §2.3.
 *
 * The Search surface's query, mode, filters, and grouping live in the URL query string
 * (frontend.md §2.2 — "URL is the source of truth for shareable/restorable surface state
 * ... a saved search is just a serialized URL"). This module is the single place that
 * serializes `SearchUrlState` ⇄ `URLSearchParams`, so the surface, the command palette,
 * and saved searches all agree on the encoding.
 *
 * The query-param grammar from frontend.md §2.3:
 *   ?q=&mode=hybrid|lexical|semantic&notebook=&tag=&entity=&from=&to=&source=&edge=&group=
 */

import type { Filters, SourceType } from '@/types';
import { asEntityId, asNoteId, asNotebookId } from '@/types';
import type { SearchMode } from '@/hooks';

export type ResultGrouping = 'flat' | 'entity' | 'time';

/**
 * Visual density of the result list (UX-reconciliation F4.2 — orthogonal to
 * {@link ResultGrouping}). `cards` is the existing roomy layout; `compact` is a
 * dense single-line row for power-user scanning.
 */
export type ResultDensity = 'cards' | 'compact';

/** The full surface state encoded in the URL. */
export interface SearchUrlState {
  query: string;
  mode: SearchMode;
  filters: Filters;
  grouping: ResultGrouping;
  /** Visual density (UX-reconciliation F4.2). Defaults to `cards`. */
  density: ResultDensity;
}

const DEFAULT_STATE: SearchUrlState = {
  query: '',
  mode: 'hybrid',
  filters: {},
  grouping: 'flat',
  density: 'cards',
};

function parseMode(raw: string | null): SearchMode {
  return raw === 'lexical' || raw === 'semantic' ? raw : 'hybrid';
}

function parseGrouping(raw: string | null): ResultGrouping {
  return raw === 'entity' || raw === 'time' ? raw : 'flat';
}

function parseDensity(raw: string | null): ResultDensity {
  return raw === 'compact' ? 'compact' : 'cards';
}

/** Decode a `URLSearchParams` into `SearchUrlState`. Unknown/missing params fall to defaults. */
export function parseSearchUrl(params: URLSearchParams): SearchUrlState {
  const filters: Filters = {};

  const notebooks = params.getAll('notebook');
  if (notebooks.length > 0) filters.notebookIds = notebooks.map((n) => asNotebookId(n));

  const tags = params.getAll('tag');
  if (tags.length > 0) filters.tagPaths = tags;

  const entities = params.getAll('entity');
  if (entities.length > 0) filters.entityIds = entities.map((e) => asEntityId(e));

  const from = params.get('from');
  if (from) filters.dateFrom = from;
  const to = params.get('to');
  if (to) filters.dateTo = to;

  const sources = params.getAll('source');
  if (sources.length > 0) filters.sources = sources as SourceType[];

  const edges = params.getAll('edge');
  const focal = params.get('focal');
  // An edge filter is only valid paired with a focal note (the backend rejects otherwise).
  if (edges.length > 0 && focal) {
    filters.edgeTypes = edges as Filters['edgeTypes'];
    filters.focalNoteId = asNoteId(focal);
  }

  if (params.get('include_deleted') === '1') filters.includeDeleted = true;

  return {
    query: params.get('q') ?? DEFAULT_STATE.query,
    mode: parseMode(params.get('mode')),
    filters,
    grouping: parseGrouping(params.get('group')),
    density: parseDensity(params.get('density')),
  };
}

/** Encode `SearchUrlState` back into a `URLSearchParams` — the inverse of `parseSearchUrl`. */
export function serializeSearchUrl(state: SearchUrlState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.query) params.set('q', state.query);
  if (state.mode !== 'hybrid') params.set('mode', state.mode);
  if (state.grouping !== 'flat') params.set('group', state.grouping);
  if (state.density !== 'cards') params.set('density', state.density);

  const { filters } = state;
  filters.notebookIds?.forEach((n) => params.append('notebook', n));
  filters.tagPaths?.forEach((t) => params.append('tag', t));
  filters.entityIds?.forEach((e) => params.append('entity', e));
  if (filters.dateFrom) params.set('from', filters.dateFrom);
  if (filters.dateTo) params.set('to', filters.dateTo);
  filters.sources?.forEach((s) => params.append('source', s));
  filters.edgeTypes?.forEach((e) => params.append('edge', e));
  if (filters.focalNoteId) params.set('focal', filters.focalNoteId);
  if (filters.includeDeleted) params.set('include_deleted', '1');

  return params;
}
